import { calculateDistance } from "./calculateDistance";

/**
 * Enriches track points with computed data like slope (gradient) directly on the point object.
 * @param {Array<object>} points - The array of GPX points.
 * @param {object} options - Configuration options.
 * @param {number} [options.smoothingWindow=2] - Number of points to look back/forward for smoothing slope.
 * @returns {Array<object>} The array of points with added properties.
 */
export const enrichTrackPoints = (points, options = {}) => {
  const { smoothingWindow = 2 } = options;

  if (!points || points.length === 0) return points;

  // Clone points to avoid mutating the original array reference if needed,
  // but usually we want to return a new array with modified objects.
  // Given performance concerns, we might mutate objects if they are already fresh copies.
  // checks upstream suggest points might be fresh from IDB or context, but let's be safe.

  // 1. Calculate Cumulative Distance first (needed for slope)
  // We will add a temporary 'distCumulative' property if it doesn't exist,
  // or use a local array to store it to avoid polluting the object if not requested,
  // but usually having 'distance' (cumulative) on the point is useful.

  let cumulativeDistance = 0;

  // Create a working copy of points to strictly follow functional patterns if feasible,
  // but for 10k+ points, in-place mutation of the *elements* of a shallow copy is often preferred in JS.
  const enrichedPoints = [...points];

  // Initialize first point
  // We'll add 'distance' (cumulative meters) and 'slope' (%)
  enrichedPoints[0].distance = 0;
  enrichedPoints[0].slope = 0;

  for (let i = 1; i < enrichedPoints.length; i++) {
    const prev = enrichedPoints[i - 1];
    const curr = enrichedPoints[i];

    // Check if lat/lng are valid numbers
    if (typeof prev.lat !== "number" || typeof curr.lat !== "number") {
      curr.distance = cumulativeDistance;
      continue;
    }

    const dist = calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    cumulativeDistance += dist;

    curr.distance = cumulativeDistance;
    curr.slope = 0; // Default, calc below
  }

  // 2. Calculate Slope (Gradient) with Smoothing and Minimum Distance
  const totalPoints = enrichedPoints.length;
  const MIN_DISTANCE_FOR_SLOPE = 15; // meters required to calculate a valid slope (reduces noise)
  const MAX_SEARCH_WINDOW = 10; // Max points to search forward/backward
  const MAX_SLOPE_DEG = 50; // Clamp values to +/- 50 degrees

  for (let i = 0; i < totalPoints; i++) {
    const currentPointDist = enrichedPoints[i].distance;

    // Find start point: look back until distance > MIN_DISTANCE / 2 or hit array start
    let startIdx = i;
    while (
      startIdx > 0 &&
      currentPointDist - enrichedPoints[startIdx].distance <
        MIN_DISTANCE_FOR_SLOPE / 2 &&
      i - startIdx < MAX_SEARCH_WINDOW
    ) {
      startIdx--;
    }

    // Find end point: look forward until distance > MIN_DISTANCE / 2 or hit array end
    let endIdx = i;
    while (
      endIdx < totalPoints - 1 &&
      enrichedPoints[endIdx].distance - currentPointDist <
        MIN_DISTANCE_FOR_SLOPE / 2 &&
      endIdx - i < MAX_SEARCH_WINDOW
    ) {
      endIdx++;
    }

    const pStart = enrichedPoints[startIdx];
    const pEnd = enrichedPoints[endIdx];

    // Use pre-calculated cumulative distance
    const distDelta = pEnd.distance - pStart.distance;
    // const eleDelta = pEnd.ele - pStart.ele; // Original

    let slope = 0;

    // Only calculate if we have a meaningful distance span
    if (
      distDelta >= MIN_DISTANCE_FOR_SLOPE &&
      // distDelta > 0 && // Implicitly true if >= MIN_DISTANCE
      typeof pEnd.ele === "number" &&
      typeof pStart.ele === "number"
    ) {
      const eleDelta = pEnd.ele - pStart.ele;
      // Calculate angle in radians: atan(rise/run)
      // Then convert to degrees: rad * (180/PI)
      const angleRad = Math.atan(eleDelta / distDelta);
      slope = angleRad * (180 / Math.PI);

      // Clamp outliers
      if (slope > MAX_SLOPE_DEG) slope = MAX_SLOPE_DEG;
      if (slope < -MAX_SLOPE_DEG) slope = -MAX_SLOPE_DEG;
    }

    // Assign slope (now angle in degrees)
    enrichedPoints[i].slope = parseFloat(slope.toFixed(1));
  }

  return enrichedPoints;
};
